// ==================== TEMPLATES SERVICE ====================

import { apiService } from './api';
import type { PaginatedResponse } from '../types';

// ==================== TYPES ====================

export interface VariableSample {
  variable: string;
  sample: string;
}

export interface CreateTemplateData {
  name: string;
  category: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
  language?: string;
  headerType?: 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT';
  headerContent?: string;
  bodyText: string;
  footerText?: string;
  buttons?: Array<{
    type: 'QUICK_REPLY' | 'URL' | 'PHONE' | 'COPY_CODE';
    text: string;
    url?: string;
    urlType?: 'STATIC' | 'DYNAMIC';
    phoneNumber?: string;
    countryCode?: string;
    offerCode?: string;
  }>;
  variableSamples?: VariableSample[];
}

export type UpdateTemplateData = Partial<CreateTemplateData>;

export interface QueryTemplatesParams {
  search?: string;
  category?: string;
  status?: string;
  language?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface TemplateStats {
  total: number;
  byStatus: {
    draft: number;
    pending: number;
    approved: number;
    rejected: number;
  };
  byCategory: Array<{
    category: string;
    count: number;
  }>;
}

export interface MediaUploadResult {
  success: boolean;
  handle?: string;
  previewUrl?: string;
  message?: string;
}

// Extended template type for frontend
export interface TemplateItem {
  id: string;
  name: string;
  category: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
  language: string;
  status: 'DRAFT' | 'PENDING_APPROVAL' | 'APPROVED' | 'REJECTED';
  waTemplateId?: string;
  headerType?: 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT';
  headerContent?: string;
  headerMediaId?: string;        // WhatsApp media ID (for sending - valid 30 days)
  mediaIdExpiresAt?: string;     // Expiration date for headerMediaId
  bodyText: string;
  footerText?: string;
  buttons?: Array<{
    type: 'QUICK_REPLY' | 'URL' | 'PHONE' | 'COPY_CODE';
    text: string;
    url?: string;
    urlType?: 'STATIC' | 'DYNAMIC';
    phoneNumber?: string;
    countryCode?: string;
    offerCode?: string;
  }>;
  variableSamples?: VariableSample[];
  createdAt: string;
  updatedAt: string;
}

// ==================== SERVICE ====================

export const templatesService = {
  // Get all templates with filtering and pagination
  async getAll(params?: QueryTemplatesParams): Promise<PaginatedResponse<TemplateItem>> {
    const queryParams = new URLSearchParams();
    
    if (params?.search) queryParams.append('search', params.search);
    if (params?.category) queryParams.append('category', params.category);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.language) queryParams.append('language', params.language);
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.limit) queryParams.append('limit', String(params.limit));
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const url = `/customer/templates${queryParams.toString() ? `?${queryParams}` : ''}`;
    const response = await apiService.get(url);
    
    // Backend returns { data: [...], meta: {...} } wrapped in { success, data: { data, meta } }
    const innerData = response.data as { data?: TemplateItem[]; meta?: PaginatedResponse<TemplateItem>['meta'] } | undefined;
    
    return {
      items: innerData?.data || [],
      meta: innerData?.meta || {
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 1,
        hasNextPage: false,
        hasPrevPage: false,
      },
    };
  },

  // Get single template
  async getById(id: string): Promise<TemplateItem> {
    const response = await apiService.get(`/customer/templates/${id}`);
    // Backend returns { data: template } wrapped in { success, data: { data: template } }
    const innerData = response.data as { data?: TemplateItem } | undefined;
    return innerData?.data as TemplateItem;
  },

  // Create template
  async create(data: CreateTemplateData): Promise<TemplateItem> {
    const response = await apiService.post('/customer/templates', data);
    
    // Check if response indicates an error
    if (!response.success && response.message) {
      throw new Error(response.message);
    }
    
    const innerData = response.data as { data?: TemplateItem } | undefined;
    return innerData?.data as TemplateItem;
  },

  // Update template
  async update(id: string, data: UpdateTemplateData): Promise<TemplateItem> {
    const response = await apiService.put(`/customer/templates/${id}`, data);
    
    // Check if response indicates an error
    if (!response.success && response.message) {
      throw new Error(response.message);
    }
    
    const innerData = response.data as { data?: TemplateItem } | undefined;
    return innerData?.data as TemplateItem;
  },

  // Delete template
  async delete(id: string): Promise<void> {
    await apiService.delete(`/customer/templates/${id}`);
  },

  // Submit template for approval
  async submitForApproval(id: string): Promise<TemplateItem> {
    const response = await apiService.post(`/customer/templates/${id}/submit`);
    const innerData = response.data as { data?: TemplateItem } | undefined;
    return innerData?.data as TemplateItem;
  },

  // Sync templates from Meta
  async syncFromMeta(): Promise<{ synced: number; updated: number }> {
    const response = await apiService.post('/customer/templates/sync');
    const innerData = response.data as { data?: { synced: number; updated: number } } | undefined;
    return innerData?.data || { synced: 0, updated: 0 };
  },

  // Get template stats
  async getStats(): Promise<TemplateStats> {
    const response = await apiService.get('/customer/templates/stats');
    const innerData = response.data as { data?: TemplateStats } | undefined;
    return innerData?.data as TemplateStats;
  },

  // Upload media for template header (IMAGE, VIDEO, DOCUMENT)
  async uploadMedia(
    file: File,
    mediaType: 'IMAGE' | 'VIDEO' | 'DOCUMENT',
  ): Promise<MediaUploadResult> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('mediaType', mediaType);

    // Use the upload method which properly handles FormData with multipart/form-data headers
    const response = await apiService.upload<MediaUploadResult>('/customer/templates/upload-media', formData);

    // The response might be wrapped in data or be at the top level
    const result = response.data || response;
    return (result as MediaUploadResult) || { success: false, message: 'Unknown error' };
  },
};

